// Eskom PARCC — working-paper notes parser (owner R3 feedback: free text is
// strangled in a narrow column / runs as a wall in the detail card).
//
// Reviewers capture notes as a semi-structured run of clause items, e.g.
//   "1. 1.1 Approved single-source justification … Result: Non-Compliant - PFMA
//    Finding: no proof … Action: follow up with the buyer 2. 1.2 …"
// We parse that best-effort into clause items with labelled Result/Finding/Action
// segments so they render as a clean list. If the text does not match the shape,
// we fall back to a plain block — so the component is always neat, structured or not.

export interface NoteItem {
  /** Clause number, e.g. "1.1". */
  clause: string;
  /** The criterion text (everything before the first Result/Finding/Action label). */
  criterion: string;
  result?: string;
  finding?: string;
  action?: string;
}

export type ParsedNotes =
  | { kind: 'structured'; items: NoteItem[] }
  | { kind: 'plain'; text: string };

// A clause item: "<counter>. <clause> <body…>" where the body runs until the next
// "<counter>. <clause>" or end of string. Clause is a dotted number (1.1, 1.2.3).
const ITEM_RE = /(\d+)\.\s+(\d+(?:\.\d+)+)\s+([\s\S]*?)(?=\s+\d+\.\s+\d+(?:\.\d+)+\s|$)/g;

const FIELD_LABELS: ReadonlyArray<readonly [keyof Pick<NoteItem, 'result' | 'finding' | 'action'>, RegExp]> = [
  ['result', /\bResult\s*:/i],
  ['finding', /\bFinding\s*:/i],
  ['action', /\bAction\s*:/i],
];

/** Split an item body into its criterion + labelled fields (any order, optional). */
function splitFields(body: string): Omit<NoteItem, 'clause'> {
  const markers: Array<{ key: 'result' | 'finding' | 'action'; start: number; end: number }> = [];
  for (const [key, re] of FIELD_LABELS) {
    const m = re.exec(body);
    if (m) markers.push({ key, start: m.index, end: m.index + m[0].length });
  }
  markers.sort((a, b) => a.start - b.start);

  const criterionEnd = markers.length > 0 ? markers[0].start : body.length;
  const out: Omit<NoteItem, 'clause'> = { criterion: body.slice(0, criterionEnd).trim() };
  for (let i = 0; i < markers.length; i++) {
    const valStart = markers[i].end;
    const valEnd = i + 1 < markers.length ? markers[i + 1].start : body.length;
    const value = body.slice(valStart, valEnd).trim();
    if (value) out[markers[i].key] = value;
  }
  return out;
}

/** Parse working-paper notes into clause items, or a plain block when unstructured. */
export function parseWorkingPaperNotes(raw: string | null | undefined): ParsedNotes {
  const text = (raw ?? '').trim();
  if (!text) return { kind: 'plain', text: '' };

  const items: NoteItem[] = [];
  for (const m of text.matchAll(ITEM_RE)) {
    const body = m[3].trim();
    items.push({ clause: m[2], ...splitFields(body) });
  }
  if (items.length === 0) return { kind: 'plain', text };
  return { kind: 'structured', items };
}

/** A short one-line snippet for the compact row view. */
export function notesSnippet(raw: string | null | undefined): string {
  const parsed = parseWorkingPaperNotes(raw);
  if (parsed.kind === 'plain') return parsed.text;
  return parsed.items.map((it) => `${it.clause} ${it.criterion}`.trim()).join(' · ');
}
